/**
 * PracticeCard Component
 *
 * Mobile-first card for quiz and sequencer practice items.
 * Shows type icon, SOP/section info, question/step count, best score, and attempt count.
 * Uses OperatorCard wrapper for consistent styling.
 *
 * Icon/color mapping:
 *   quiz      -> Brain (cyan)
 *   sequencer -> Gamepad2 (rose)
 */

import { Brain, Gamepad2, ArrowRight } from 'lucide-react';
import { OperatorCard } from './OperatorCard';
import { getScoreColor } from '../../../lib/score-colors';

interface PracticeCardProps {
  type: 'quiz' | 'sequencer';
  /** sectionId (quiz) or taskId (sequencer) */
  targetId: string;
  /** SOP section title (quiz) or task title (sequencer) */
  title: string;
  /** SOP name */
  sopTitle: string;
  /** Number of questions (quiz) or steps (sequencer) */
  itemCount: number;
  /** Best score percentage (null if never attempted) */
  bestScore: number | null;
  /** Number of completed attempts */
  attemptCount: number;
  /** Animation index for staggered fade-in */
  index?: number;
}

export function PracticeCard({
  type,
  targetId,
  title,
  sopTitle,
  itemCount,
  bestScore,
  attemptCount,
  index = 0,
}: PracticeCardProps) {
  const isQuiz = type === 'quiz';

  const href = isQuiz
    ? `/operator/practice/quiz/${targetId}`
    : `/operator/practice/sequencer/${targetId}`;

  const Icon = isQuiz ? Brain : Gamepad2;
  const iconBg = isQuiz ? 'bg-cyan-100' : 'bg-rose-100';
  const iconColor = isQuiz ? 'text-cyan-600' : 'text-rose-600';
  const typeLabel = isQuiz ? 'Quiz' : 'Step Sequencer';
  const countLabel = isQuiz
    ? `${itemCount} questions`
    : `${itemCount} steps`;

  // Estimate ~2 min for a quiz session
  const timeEstimate = isQuiz ? ' \u00b7 ~2 min' : '';

  return (
    <div
      style={{
        animation: `fadeInUp 0.4s ease-out ${index < 5 ? index * 0.06 : 0}s both`,
      }}
    >
      <OperatorCard href={href}>
        <div className="flex items-center gap-3">
          {/* Type icon */}
          <div
            className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${iconBg} ${iconColor}`}
          >
            <Icon size={22} strokeWidth={1.5} />
          </div>

          {/* Content */}
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex items-start justify-between gap-2">
              <h3 className="text-sm font-semibold text-op-text-primary line-clamp-1">
                {title}
              </h3>
              {bestScore !== null && (
                <span
                  className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${getScoreColor(bestScore, 'badge')}`}
                >
                  Best: {bestScore}%
                </span>
              )}
            </div>

            <p className="text-xs text-op-text-secondary line-clamp-1">{sopTitle}</p>

            {/* Meta row */}
            <div className="mt-1 flex items-center gap-2 text-xs text-op-text-tertiary">
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                  isQuiz ? 'bg-cyan-50 text-cyan-600' : 'bg-rose-50 text-rose-600'
                }`}
              >
                {typeLabel}
              </span>
              <span>
                {countLabel}{timeEstimate}
              </span>
              {attemptCount > 0 && (
                <span className="text-op-text-tertiary">
                  {attemptCount} {attemptCount === 1 ? 'attempt' : 'attempts'}
                </span>
              )}
            </div>
          </div>

          {/* Arrow */}
          <ArrowRight size={16} strokeWidth={1.5} className="flex-shrink-0 text-op-text-tertiary" />
        </div>
      </OperatorCard>
    </div>
  );
}
